import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { IconName } from 'src/app/helpers/icon-exports';

export interface HeaderTab {
  id?: number;
  text: string;
  routerLink: string;
  icon?: IconName;
  disabled?: boolean;
  hasNoPermission?: boolean;
  activeRoutes?: string[];
}

@Component({
    selector: 'app-page-nav-bar',
    imports: [CommonModule, RouterModule, SvgIconComponent],
    templateUrl: './page-nav-bar.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./page-nav-bar.component.css']
})
export class PageNavBarComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() tabs: HeaderTab[] = [];
  @Input() secondaryTabs?: HeaderTab[];

  @ViewChild('primaryRow') primaryRowRef!: ElementRef<HTMLElement>;
  @ViewChild('measureLayer') measureLayerRef!: ElementRef<HTMLElement>;
  @ViewChild('secondaryRow') secondaryRowRef?: ElementRef<HTMLElement>;
  @ViewChild('secondaryMeasureLayer') secondaryMeasureLayerRef?: ElementRef<HTMLElement>;

  visibleTabs: HeaderTab[] = [];
  overflowTabs: HeaderTab[] = [];
  moreOpen = false;

  secondaryVisibleTabs: HeaderTab[] = [];
  secondaryOverflowTabs: HeaderTab[] = [];
  secondaryMoreOpen = false;

  private moreCloseTimer?: ReturnType<typeof setTimeout>;
  private secondaryMoreCloseTimer?: ReturnType<typeof setTimeout>;
  private resizeObserver?: ResizeObserver;
  private docClickFn!: (e: MouseEvent) => void;
  private routerSub?: Subscription;

  currentUrl = this.router.url;

  constructor(private ngZone: NgZone, private router: Router) {
    this.routerSub = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => { this.currentUrl = e.urlAfterRedirects; });
  }

  get permittedTabs(): HeaderTab[] {
    return this.tabs.filter(t => !t.hasNoPermission);
  }

  get permittedSecondaryTabs(): HeaderTab[] {
    return (this.secondaryTabs ?? []).filter(t => !t.hasNoPermission);
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.ngZone.run(() => {
        this.recalcTabs();
        this.recalcSecondaryTabs();
      });
    });
    this.resizeObserver.observe(this.primaryRowRef.nativeElement);

    this.docClickFn = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!target.closest('.pnb-more-wrap')) {
        this.ngZone.run(() => {
          this.moreOpen = false;
          this.secondaryMoreOpen = false;
        });
      }
    };
    document.addEventListener('click', this.docClickFn);
    setTimeout(() => {
      this.recalcTabs();
      this.recalcSecondaryTabs();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabs']) {
      setTimeout(() => this.recalcTabs());
    }
    if (changes['secondaryTabs']) {
      setTimeout(() => this.recalcSecondaryTabs());
    }
  }

  recalcTabs(): void {
    const bar = this.primaryRowRef?.nativeElement;
    const measure = this.measureLayerRef?.nativeElement;
    if (!bar || !measure) return;

    const style = window.getComputedStyle(bar);
    const paddingH = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    const containerW = bar.offsetWidth - paddingH;
    if (containerW === 0) return;

    const permitted = this.permittedTabs;
    const moreBtnW = 110;
    const tabEls = Array.from(measure.children) as HTMLElement[];

    let usedW = 0;
    let cutoff = permitted.length;

    for (let i = 0; i < tabEls.length; i++) {
      const tabW = tabEls[i].offsetWidth;
      if (usedW + tabW > containerW - moreBtnW) {
        cutoff = i;
        break;
      }
      usedW += tabW;
    }

    this.visibleTabs = permitted.slice(0, cutoff);
    this.overflowTabs = permitted.slice(cutoff);
  }

  recalcSecondaryTabs(): void {
    const row = this.secondaryRowRef?.nativeElement;
    const measure = this.secondaryMeasureLayerRef?.nativeElement;
    if (!row || !measure) return;

    const style = window.getComputedStyle(row);
    const paddingH = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    const containerW = row.offsetWidth - paddingH;
    if (containerW === 0) return;

    const permitted = this.permittedSecondaryTabs;
    const moreBtnW = 100;
    const gap = 4;
    const tabEls = Array.from(measure.children) as HTMLElement[];

    let usedW = 0;
    let cutoff = permitted.length;

    for (let i = 0; i < tabEls.length; i++) {
      const tabW = tabEls[i].offsetWidth + gap;
      if (usedW + tabW > containerW - moreBtnW) {
        cutoff = i;
        break;
      }
      usedW += tabW;
    }

    this.secondaryVisibleTabs = permitted.slice(0, cutoff);
    this.secondaryOverflowTabs = permitted.slice(cutoff);
  }

  openMore(): void { clearTimeout(this.moreCloseTimer); this.moreOpen = true; }
  scheduleCloseMore(): void { this.moreCloseTimer = setTimeout(() => this.moreOpen = false, 120); }
  cancelCloseMore(): void { clearTimeout(this.moreCloseTimer); }

  openSecondaryMore(): void { clearTimeout(this.secondaryMoreCloseTimer); this.secondaryMoreOpen = true; }
  scheduleCloseSecondaryMore(): void { this.secondaryMoreCloseTimer = setTimeout(() => this.secondaryMoreOpen = false, 120); }
  cancelCloseSecondaryMore(): void { clearTimeout(this.secondaryMoreCloseTimer); }

  isTabActive(tab: HeaderTab): boolean {
    const url = this.currentUrl;
    const link = tab.routerLink ?? '';
    const linkMatches = link.startsWith('/') ? url.includes(link) : url.includes('/' + link);
    return linkMatches || (tab.activeRoutes?.some(r => url.includes(r)) ?? false);
  }

  get isOverflowActive(): boolean {
    return this.overflowTabs.some(tab => this.isTabActive(tab));
  }

  get isSecondaryOverflowActive(): boolean {
    return this.secondaryOverflowTabs.some(tab => this.isTabActive(tab));
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.routerSub?.unsubscribe();
    clearTimeout(this.moreCloseTimer);
    clearTimeout(this.secondaryMoreCloseTimer);
    if (this.docClickFn) {
      document.removeEventListener('click', this.docClickFn);
    }
  }
}
